import React, { useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Search, LayoutDashboard, FileText, Users, ShieldAlert, MapPin,
  Megaphone, Bell, Video, MessageSquare, Radio, ScrollText, Shield,
  Ban, Camera, EyeOff, ChevronLeft, ChevronRight, Mail, Crown, Mic, Music,
} from 'lucide-react';

/**
 * Two-tier admin nav.
 *
 * Groups tabs by purpose so nothing is more than one click away and the
 * horizontal scroll is gone. Collapsible on desktop (icons-only mode) and
 * turns into a slide-over drawer on mobile.
 *
 * The parent still owns `activeTab` - this component just displays / filters.
 */

const SECTIONS = [
  {
    label: 'Dashboard',
    items: [
      { id: 'overview', label: 'Overview', Icon: LayoutDashboard },
      { id: 'analytics', label: 'Analytics', Icon: Radio },
      { id: 'geography', label: 'Geography', Icon: MapPin },
    ],
  },
  {
    label: 'Investigations',
    items: [
      { id: 'experiences', label: 'The Madame Files', Icon: ShieldAlert, accent: true },
      { id: 'private', label: 'Private (Honeypot)', Icon: EyeOff },
    ],
  },
  {
    label: 'Content',
    items: [
      { id: 'articles', label: 'Articles', Icon: FileText },
      { id: 'ticker', label: 'Breaking Ticker', Icon: Radio },
      { id: 'quotes', label: 'Intro Quotes', Icon: ScrollText },
      { id: 'videos', label: '18+ Videos', Icon: Video },
      { id: 'audio', label: 'Audio & Podcast', Icon: Music, accent: true },
    ],
  },
  {
    label: 'Community',
    items: [
      { id: 'tipwall', label: 'Tip Wall', Icon: MessageSquare },
      { id: 'voicemail', label: 'Voicemails', Icon: Mic, accent: true },
      { id: 'article-comments', label: 'Article Comments', Icon: MessageSquare },
      { id: 'comments', label: 'Video Comments', Icon: MessageSquare },
      { id: 'video-views', label: 'Video Views', Icon: Video },
      { id: 'crimemap', label: 'Crime Map', Icon: MapPin },
    ],
  },
  {
    label: 'Users',
    items: [
      { id: 'accounts', label: 'Accounts', Icon: Users, accent: true },
      { id: 'subscribers', label: 'Subscribers', Icon: Users },
      { id: 'founders', label: 'Founders', Icon: Crown, accent: true },
      { id: 'broadcasts', label: 'Email Broadcasts', Icon: Mail, accent: true },
      { id: 'push', label: 'Push Notifications', Icon: Bell },
    ],
  },
  {
    label: 'Growth',
    items: [
      { id: 'ads', label: 'Ads & Reports', Icon: Megaphone },
    ],
  },
  {
    label: 'Security',
    items: [
      { id: 'blocklist', label: 'IP Blocklist', Icon: Ban },
      { id: 'banned-emails', label: 'Banned Emails', Icon: Ban },
      { id: 'triggers', label: 'Auto-Ban Triggers', Icon: Shield },
    ],
  },
];

const AdminTabs = ({ activeTab, onChange, onLogout }) => {
  const [q, setQ] = useState('');
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const filtered = useMemo(() => {
    if (!q.trim()) return SECTIONS;
    const needle = q.toLowerCase();
    return SECTIONS
      .map((s) => ({ ...s, items: s.items.filter((i) => i.label.toLowerCase().includes(needle)) }))
      .filter((s) => s.items.length > 0);
  }, [q]);

  const active = SECTIONS.flatMap((s) => s.items).find((i) => i.id === activeTab);

  const handleChange = (id) => {
    onChange(id);
    setMobileOpen(false);
  };

  return (
    <>
      {/* Mobile header - always visible on <lg, shows current tab + hamburger */}
      <div className="lg:hidden sticky top-0 z-30 bg-[#0a0a0a] border-b border-zinc-800 flex items-center justify-between px-4 py-3">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] text-zinc-500 uppercase tracking-widest">Admin</p>
          <p className="text-white font-black text-sm truncate">{active?.label || 'Menu'}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="text-white bg-red-600 hover:bg-red-500 px-3 py-2 text-xs font-black uppercase tracking-widest"
            data-testid="admin-nav-open"
          >
            Menu
          </button>
          {onLogout && (
            <button
              type="button"
              onClick={onLogout}
              className="text-zinc-300 hover:text-white bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 px-3 py-2 text-xs font-black uppercase tracking-widest"
              data-testid="admin-mobile-logout-btn"
            >
              Exit
            </button>
          )}
        </div>
      </div>

      {/* Sidebar - always visible on lg+, drawer on mobile */}
      <aside
        className={`fixed lg:sticky top-0 lg:top-0 left-0 z-40 h-screen ${
          collapsed ? 'w-16' : 'w-64'
        } bg-[#0d0d0f] border-r border-zinc-900 flex flex-col transition-all duration-200 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        data-testid="admin-sidebar"
      >
        {/* Header */}
        <div className="border-b border-zinc-900 p-4">
          <div className="flex items-center justify-between">
            {!collapsed && (
              <div>
                <p className="text-red-500 text-[10px] font-black uppercase tracking-[0.3em]">Outlaw</p>
                <p className="text-white font-black text-sm">Admin Console</p>
              </div>
            )}
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setCollapsed((v) => !v)}
                className="hidden lg:flex p-1 text-zinc-500 hover:text-white"
                title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
              </button>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="lg:hidden p-1 text-zinc-500 hover:text-white"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            </div>
          </div>
          {!collapsed && (
            <div className="mt-3 relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search sections…"
                className="w-full bg-[#0a0a0a] border border-zinc-800 focus:border-red-500 rounded pl-7 pr-2 py-1.5 text-xs text-white outline-none placeholder:text-zinc-600"
                data-testid="admin-nav-search"
              />
            </div>
          )}
        </div>

        {/* Scrolling group list */}
        <nav className="flex-1 overflow-y-auto py-3">
          {filtered.map((section) => (
            <div key={section.label} className="mb-4">
              {!collapsed && (
                <p className="px-4 mb-1 text-[9px] text-zinc-600 font-black uppercase tracking-[0.25em]">
                  {section.label}
                </p>
              )}
              {section.items.map(({ id, label, Icon, accent }) => {
                const isActive = id === activeTab;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => handleChange(id)}
                    data-testid={`admin-tab-${id}`}
                    className={`w-full flex items-center gap-2.5 px-4 py-2 text-left transition-colors border-l-2 ${
                      isActive
                        ? 'bg-red-600/10 border-red-500 text-white'
                        : `border-transparent ${accent ? 'text-red-400' : 'text-zinc-400'} hover:bg-zinc-900/50 hover:text-white`
                    }`}
                    title={collapsed ? label : undefined}
                  >
                    <Icon className={`h-4 w-4 shrink-0 ${accent && !isActive ? 'text-red-500' : ''}`} />
                    {!collapsed && <span className="text-sm font-medium truncate">{label}</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Back to site */}
        {!collapsed && (
          <NavLink
            to="/"
            className="border-t border-zinc-900 px-4 py-3 text-zinc-500 hover:text-white text-xs uppercase tracking-widest"
          >
            ← Back to site
          </NavLink>
        )}
      </aside>

      {/* Mobile scrim */}
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          className="lg:hidden fixed inset-0 z-30 bg-black/60"
        />
      )}
    </>
  );
};

export default AdminTabs;
